/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LoadingService, LogService, UserService } from '../../../../../../core/services';
import { OrganisationsApiService, PortalRbacApiService } from '../../../../shared/http';
import { PermissionsEnum } from '../../../../shared/enums';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-portal-roles',
    templateUrl: './portal-add-edit-roles.component.html'
})
export class PortalAddEditRolesComponent extends BaseComponent {
    /**
     * Api data for RBAC roles
     */
    public data: any = null;

    /**
     * Grouping of endpoints
     */
    public grouping: any[] = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private loadingService: LoadingService,
        private logService: LogService,
        private organisationsApiService: OrganisationsApiService,
        private portalRbacApiService: PortalRbacApiService,
        private router: Router,
        private userService: UserService
    ) {
        super();

        const id: string = this.activatedRoute.snapshot.paramMap.get('id');
        const duplicate: boolean = !!this.activatedRoute.snapshot.paramMap.get('duplicate');
        const editId: number = id ? parseInt(id, 10) : null;
        this.grouping = this.getGrouping();
        this.setUpPageData(editId, duplicate);
    }

    /**
     * Submit
     * @param roleValues
     */
    public submit(roleValues: any): void {
        const role: any = {
            id: roleValues['id'],
            name: roleValues['name'],
            endpoints: this.cleanEndpoints(roleValues['endpoints']),
            groups: roleValues['groups'],
            users: roleValues['users']
        };

        const apiCall: Observable<any> = role.id
            ? this.portalRbacApiService.editRole(role.id, role)
            : this.portalRbacApiService.createRole(role);

        apiCall
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.router.navigate(['/cm/organisation/users']).then().catch();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PortalAddEditRolesComponent: submit', error.message);
                    this.loadingService.loadingButtonEmitter.next(false);
                }
            });
    }

    /**
     * Format endpoints
     * @param endpoints
     * @private
     */
    private cleanEndpoints(endpoints: any[]): any[] {
        const cleanEndpoints: any[] = [];
        for (const endpoint of endpoints) {
            const newEndpoint: any = {
                path: endpoint.path
            };

            const methods = endpoint.methods;
            if (methods) {
                const newMethod: any[] = [];
                for (const method in methods) {
                    if (methods.hasOwnProperty(method)) {
                        if (methods[method] === true) {
                            newMethod.push(method);
                        }
                    }
                }
                newEndpoint['methods'] = newMethod;
            }
            cleanEndpoints.push(newEndpoint);
        }

        return cleanEndpoints;
    }

    /**
     * Get grouping.
     * @private
     */
    private getGrouping(): any[] {
        const userPermissions = this.userService.getUser('permissions');

        let grouping = [
            { service: 'adcs', path: '/api/v1/:namespace/adcs/', friendlyName: 'ADCs' },
            { service: 'backups', path: '/api/v1/:namespace/backups/', friendlyName: 'Backups' },
            { service: 'scheduler', path: '/api/v1/:namespace/scheduler/', friendlyName: 'Scheduler' },
            { service: 'certificates', path: '/api/v1/:namespace/security/certificates', friendlyName: 'Certificates' },
            { service: 'insights', path: '/api/v1/:namespace/security/insights', friendlyName: 'Insights' },
            { service: 'shuttles', path: '/api/v1/:namespace/sidecars/', friendlyName: 'Shuttles' },
            { service: 'shuttles', path: '/api/v1/accounts/:id/groups/', friendlyName: 'RBAC' },
            { service: 'shuttles', path: '/api/v1/accounts/:id/roles/', friendlyName: 'RBAC' },
            { service: 'namespaces', path: '/api/v1/organisations/:orgid/namespaces', friendlyName: 'Namespaces' },
            { service: 'accounts', path: '/api/v1/accounts/', friendlyName: 'Accounts' },
            { service: 'organisations', path: '/api/v1/organisations/', friendlyName: 'Organisations' },
            { service: 'support', path: '/api/v1/support/tickets/', friendlyName: 'Support' }
        ];

        if (userPermissions === PermissionsEnum.Owner || userPermissions === PermissionsEnum.Admin) {
            const ownersGrouping = [
                { service: 'rbac', path: '/api/v1/roles/', friendlyName: 'RBAC' },
                { service: 'rbac', path: '/api/v1/groups/', friendlyName: 'RBAC' },
                { service: 'rbac', path: '/api/v1/endpoints/', friendlyName: 'RBAC' },
                { service: 'rbac', path: '/api/v1/routes', friendlyName: 'RBAC' },
                { service: 'rbac', path: '/api/v1/resources/', friendlyName: 'RBAC' },
                { service: 'rbac', path: '/api/v1/features/', friendlyName: 'RBAC' }
            ];

            grouping = [...grouping, ...ownersGrouping];
        }

        if (userPermissions === PermissionsEnum.Admin) {
            const adminGrouping = [
                { service: 'admin', path: '/api/v1/tiers/', friendlyName: 'Admin' },
                { service: 'admin', path: '/api/v1/token', friendlyName: 'Admin' },
                { service: 'admin', path: '/api/v1/ping', friendlyName: 'Admin' },
                { service: 'admin', path: '/ping/', friendlyName: 'Admin' },
                { service: 'admin', path: '/ws', friendlyName: 'Admin' }
            ];

            grouping = [...grouping, ...adminGrouping];
        }

        return grouping;
    }

    /**
     * Set up page data for edit or duplicate.
     * @param id
     * @param duplicate
     * @private
     */
    private setUpPageData(id: number, duplicate: boolean): void {
        const organisationId = this.userService.getUser('organisationId');
        const apiCalls: any = {
            users: this.organisationsApiService.getMembers(organisationId),
            roles: this.portalRbacApiService.getRoles(),
            groups: this.portalRbacApiService.getGroups(),
            endpoints: this.portalRbacApiService.getAvailableEndpoints()
        };

        if (id) {
            apiCalls['rolePatch'] = this.portalRbacApiService.getRole(id.toString());
        }

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    const formattedEndpoints: any[] = this.formatEndpoints(dataGroups['endpoints']);
                    dataGroups['endpoints'] = formattedEndpoints;
                    dataGroups['isDuplicate'] = duplicate;
                    if (id && dataGroups.rolePatch) {
                        dataGroups['rolePatch']['endpoints'] = this.formatUserEndpoints(
                            formattedEndpoints, dataGroups.rolePatch.endpoints
                        );
                    }
                    this.data = dataGroups;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PortalAddEditRolesComponent: setUpPageData', error.message);
                }
            });
    }

    /**
     * Format backend data to be the same as current users endpoints.
     * @param endpoints
     * @private
     */
    private formatEndpoints(endpoints: any[]): any[] {
        const newEndpoints: any[] = [];
        const endpointsToExclude: string[] = [
            '/api/v1/:namespace/adcs/:id/warp/:loc', '/api/v1/organisations/:id/accounts/', '/api/v1/organisations/:orgid/saml'
        ];
        for (const endpoint of endpoints) {
            if (!endpointsToExclude.includes(endpoint.path)) {
                const methods = endpoint.methods;
                const newEndpoint: any = {
                    path: endpoint.path,
                    service: this.getServiceName(endpoint.path),
                    methods: this.formatMethods(methods),
                    friendlyName: this.getFriendlyName(endpoint.path)
                };

                newEndpoints.push(newEndpoint);
            }
        }

        return newEndpoints;
    }

    /**
     * Format methods
     * @param methods
     * @private
     */
    private formatMethods(methods: any[]): any {
        const newMethods: object = {};
        if (methods.length) {
            for (const method of methods) {
                newMethods[method] = false;
            }

            return newMethods;
        }
    }

    /**
     * Format user endpoints.
     * @param endpoints
     * @param userEndpoints
     * @private
     */
    private formatUserEndpoints(endpoints: any[], userEndpoints: any): any[] {
        const newEndpoints: any[] = [];
        for (const endpoint of endpoints) {
            const newEndpoint: any = {
                path: endpoint.path,
                methods: this.getUserMethods(userEndpoints, endpoint) || endpoint.methods,
                service: this.getServiceName(endpoint.path),
                friendlyName: endpoint.friendlyName,
            };

            newEndpoints.push(newEndpoint);
        }

        return newEndpoints;
    }

    /**
     * Get new Method.
     * @param userEndpoints
     * @param endpoint
     * @private
     */
    private getUserMethods(userEndpoints: any[], endpoint: { methods: any; path: any; }): any {
        const methods = endpoint.methods;
        const newMethod: object = {};
        const userMethods = userEndpoints.find((item: any): boolean => item.path === endpoint.path)?.methods;
        if (userMethods) {
            const cleanMethods = userMethods.filter((item: string): boolean => item !== '');

            for (const method in methods) {
                if (methods.hasOwnProperty(method)) {
                    newMethod[method] = cleanMethods.findIndex((item: string): boolean => item === method) !== -1;
                }
            }

            return newMethod;
        }
    }

    /**
     * Get service name to be consistent with Endurance.
     * @param path
     * @private
     */
    private getServiceName(path: string): string {
        const match: any = this.grouping.find((item: any) => path.startsWith(item.path));

        return match?.friendlyName ? match.friendlyName.toLowerCase() : 'unassigned';
    }

    /**
     * Get friendly names.
     * @param path
     * @private
     */
    private getFriendlyName(path: string): string {
        const friendlyName = {
            '/api/v1/accounts/': 'Create account',
            '/api/v1/accounts/auth/:provider': 'List SSO providers',
            '/api/v1/accounts/auth/:provider/callback': '',
            '/api/v1/accounts/auth/saml': 'Set up SAML',
            '/api/v1/accounts/auth/saml/callback': 'Login with SAML',
            '/api/v1/accounts/:id': 'Edit account',
            '/api/v1/accounts/:id/2fa/disable': 'Disabled two factor authentication',
            '/api/v1/accounts/:id/2fa/setup': 'Set up two factor authentication',
            '/api/v1/accounts/:id/2fa/verify': 'Verify two factor authentication',
            '/api/v1/accounts/:id/auth': 'Disable SSO',
            '/api/v1/accounts/:id/groups/': 'List or update an account\'s group(s)',
            '/api/v1/accounts/:id/groups/add': 'Add a group to an account',
            '/api/v1/accounts/:id/groups/remove': 'Remove a group from an account',
            '/api/v1/accounts/:id/roles/': 'List or update an account\'s role(s)',
            '/api/v1/accounts/:id/roles/add': 'Add a role to an account',
            '/api/v1/accounts/:id/roles/remove': 'Remove a role from an account',
            '/api/v1/accounts/recovery': 'Recover password', // TODO where is this?
            '/api/v1/groups/': 'Create or list groups',
            '/api/v1/groups/:id': 'Edit or delete a group',
            '/api/v1/groups/:id/accounts/': 'List, delete or update accounts belonging to a group',
            '/api/v1/groups/:id/accounts/add': 'Add an account to a group',
            '/api/v1/groups/:id/accounts/remove': 'Remove an account from a group',
            '/api/v1/groups/:id/roles/': 'List, delete or update accounts belonging to a role',
            '/api/v1/groups/:id/roles/add': 'Add an account to a role',
            '/api/v1/groups/:id/roles/remove': 'Remove an account from a role',
            '/api/v1/:namespace/adcs/': 'Add or list ADCs',
            '/api/v1/:namespace/adcs/:adcID/backup': 'Back up an ADC',
            '/api/v1/:namespace/adcs/:id': 'Edit or delete an ADC',
            '/api/v1/:namespace/adcs/:id/updates': 'Software update an ADC',
            '/api/v1/:namespace/backups/': 'List backups',
            '/api/v1/:namespace/backups/:backupID': 'Delete backup',
            '/api/v1/:namespace/backups/:backupID/download': 'Download a backup',
            '/api/v1/:namespace/scheduler/': 'Add or list schedules',
            '/api/v1/:namespace/scheduler/:id': 'Update or delete a schedule',
            '/api/v1/:namespace/security/certificates': 'List all certificates',
            '/api/v1/:namespace/security/certificates/:adcid': 'List certificates for a specific ADC',
            '/api/v1/:namespace/security/insights': 'List security insights',
            '/api/v1/:namespace/security/insights/:adcid': 'List security insights for a specific ADC',
            '/api/v1/:namespace/sidecars/': 'List shuttles',
            '/api/v1/:namespace/sidecars/:id': 'Edit or delete a shuttle',
            '/api/v1/:namespace/sidecars/:id/adopt': 'Adopt a shuttle',
            '/api/v1/:namespace/sidecars/:id/subnets': 'Add subnet to a shuttle',
            '/api/v1/:namespace/sidecars/:id/subnets/id': 'Delete a shuttle\'s subnet',
            '/api/v1/organisations/': 'Create an organisation',
            '/api/v1/organisations/:id': 'Update an organisation',
            '/api/v1/organisations/:id/accounts/:memberid': 'List or update an account',
            '/api/v1/organisations/:id/:memberid': 'Delete a member from this organisation',
            '/api/v1/organisations/:id/members': 'List an organisation\'s members',
            '/api/v1/organisations/:orgid/audit': 'List audit logs',
            '/api/v1/organisations/:orgid/namespaces': 'Create or list namespaces',
            '/api/v1/organisations/:orgid/namespaces/:id': 'Edit or delete a namespace',
            '/api/v1/resources/': 'List resources',
            '/api/v1/roles/': 'Create or list roles',
            '/api/v1/roles/:id': 'Edit or delete a role',
            '/api/v1/roles/:id/accounts/': 'List, delete or update accounts belonging to a role',
            '/api/v1/roles/:id/accounts/add': 'Add an account to a role',
            '/api/v1/roles/:id/accounts/remove': 'Remove an account from a role',
            '/api/v1/roles/:id/endpoints/': 'List, delete or update endpoints belonging to a role',
            '/api/v1/roles/:id/endpoints/add': 'Add an endpoint to a role',
            '/api/v1/roles/:id/endpoints/remove': 'Remove an endpoint from a role',
            '/api/v1/roles/:id/groups/': 'List, delete or update groups belonging to a role',
            '/api/v1/roles/:id/groups/add': 'Add a group to a role',
            '/api/v1/roles/:id/groups/remove': 'Remove a group from a role',
            '/api/v1/support/tickets/': 'Create or list support tickets',
            '/api/v1/support/tickets/:id': 'Edit or close a ticket',
            '/api/v1/support/tickets/:id/comments/': 'List or add a comment to a ticket',
        };

        return friendlyName[path] !== '' ? friendlyName[path] : null;
    }
}
